import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import { getBasketData } from 'services/getBasketData';
import { FetchInactiveBasketPayload, InactiveBasketsState } from 'types/baskets';
import { selectSelectedParishId } from 'redux/selectors/parishContext';

type InactiveState = InactiveBasketsState & { loadedParishId: string | null };

const initialState: InactiveState = {
    records: [],
    loading: false,
    error: null,
    loadMore: false,
    loadedParishId: null,
};

export const fetchInactiveBaskets = createAsyncThunkWrapper<any, FetchInactiveBasketPayload>(
    'inactiveBaskets/fetchInactiveBaskets',
    async ({ headers, name, companyId, parishId }: FetchInactiveBasketPayload, { getState }) => {
        const { pageSize, pageNumber } = headers;
        const resolvedParishId = parishId || companyId || selectSelectedParishId(getState() as any);
        if (!resolvedParishId) {
            return;
        }
        const response = await getBasketData({
            status: ['PENDING', 'CLOSED'],
            parishId: resolvedParishId,
            headers,
            name,
        });
        if (!response) return;
        return { response, pageNumber, pageSize, parishId: resolvedParishId };
    }
);

const inactiveBasketsSlice = createSlice({
    name: 'inactiveBaskets',
    initialState,
    reducers: {
        resetInactiveBaskets: () => ({ ...initialState }),
    },
    extraReducers: (builder) => {
        builder.addCase(fetchInactiveBaskets.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchInactiveBaskets.fulfilled, (state, action) => {
            state.loading = false;
            const { payload } = action || {};
            if (!Array.isArray(payload?.response?.events)) return;

            const { response, pageNumber, pageSize = 0, parishId } = payload || {};
            const { events, loadMore, total } = response || {};
            if (pageNumber !== 0 && state.loadedParishId !== parishId) return;

            if (typeof loadMore === 'boolean') {
                state.loadMore = loadMore;
            } else {
                state.loadMore = typeof total === 'number' ? (pageNumber + 1) * pageSize < total : events?.length >= pageSize;
            }

            if (pageNumber === 0) {
                state.loadedParishId = parishId;
                state.records = events || [];
            } else {
                state.records = [...(state?.records || []), ...(events || [])];
            }
        });
        builder.addCase(fetchInactiveBaskets.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || null;
        });
    },
});

export const { resetInactiveBaskets } = inactiveBasketsSlice.actions;
export default inactiveBasketsSlice.reducer;
